import React from "react";
import { useState, useRef, useEffect } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { STARTER_TEMPLATES } from "@/lib/templates";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

/* ────────────────────────────────────────────────────
   Builder Entry — Create Page (Lavender Dream)
   ──────────────────────────────────────────────────── */

const GRADIENT_BG = `
  radial-gradient(ellipse 90% 70% at 5% -5%, rgba(175, 158, 252, 0.6) 0%, transparent 55%),
  radial-gradient(ellipse 70% 60% at 95% 8%, rgba(218, 148, 238, 0.45) 0%, transparent 52%),
  radial-gradient(ellipse 55% 45% at 50% 105%, rgba(155, 195, 245, 0.35) 0%, transparent 55%),
  radial-gradient(ellipse 45% 35% at 85% 55%, rgba(245, 158, 182, 0.28) 0%, transparent 48%),
  #ede9f5
`;

// Generate a consistent hue from the first character of a string
// Constrained to brand palette: purples, lavenders, indigos (220-300)
function getHueFromString(str: string): number {
  const char = (str || "A").charAt(0).toUpperCase();
  const base = (char.charCodeAt(0) - 65) % 26;
  return 220 + (base * 80 / 26); // Maps A-Z to 220-300 range
}

// Generate relative time string from ISO date
function getRelativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

interface RecentProject {
  id: string;
  name: string;
  description: string | null;
  updated_at: string;
  isLive: boolean;
  fileCount: number;
}

interface BuilderEntryStateProps {
  onSubmit: (prompt: string) => void;
  isLoading?: boolean;
  initialPrompt?: string;
}

export function BuilderEntryState({ onSubmit, isLoading, initialPrompt = "" }: BuilderEntryStateProps) {
  const [prompt, setPrompt] = useState(initialPrompt);
  const [mode, setMode] = useState<"build" | "chat">("build");
  const [recentProjects, setRecentProjects] = useState<RecentProject[]>([]);
  const [inspirationIdeas, setInspirationIdeas] = useState<Array<{ id: string; title: string; build_prompt: string }>>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  // Fetch user's recent projects (top 6) with file counts
  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      const { data: projects } = await supabase
        .from("code_projects")
        .select("id, name, description, updated_at, deploy_url")
        .eq("user_id", user.id)
        .order("updated_at", { ascending: false })
        .limit(6);

      if (!projects || projects.length === 0) return;

      // Fetch file counts for each project from storage
      const projectsWithCounts = await Promise.all(
        projects.map(async (p) => {
          let fileCount = 0;
          try {
            const { data: files } = await supabase.storage
              .from("code-workspaces")
              .list(`${user.id}/${p.id}`);
            fileCount = files?.filter(f => !f.name.startsWith(".")).length || 0;
          } catch {
            // Storage access failed
          }
          return {
            id: p.id,
            name: p.name,
            description: p.description,
            updated_at: p.updated_at,
            isLive: !!(p as any).deploy_url,
            fileCount,
          };
        })
      );

      setRecentProjects(projectsWithCounts);
    })();
  }, [user?.id]);

  useEffect(() => {
    if (initialPrompt) setPrompt(initialPrompt);
  }, [initialPrompt]);

  // Fetch dynamic inspiration ideas
  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase
          .from("ideas")
          .select("id, title, ai_prompts")
          .eq("is_public", true)
          .gt("opportunity_score", 78)
          .limit(20);
        if (!data || data.length === 0) return;
        const shuffled = data.sort(() => Math.random() - 0.5).slice(0, 4);
        setInspirationIdeas(shuffled.map(idea => ({
          id: idea.id,
          title: idea.title,
          build_prompt: (idea.ai_prompts as any)?.build_app || `Build an app for: ${idea.title}`,
        })));
      } catch {
        // Fail silently
      }
    })();
  }, []);

  const requireAuth = () => {
    if (!user) {
      toast("Sign in to start building");
      navigate("/auth?next=/create");
      return true;
    }
    return false;
  };

  const handleSubmit = () => {
    if (requireAuth()) return;
    if (prompt.trim() && !isLoading) {
      onSubmit(prompt.trim());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-6" style={{ background: GRADIENT_BG }}>
      <div className="w-full max-w-[680px]">
        {/* Heading */}
        <div className="text-center mb-8">
          <h1
            className="text-[36px] text-[#18151f] mb-3"
            style={{ fontFamily: "'Instrument Serif', serif" }}
          >
            What do you want to{" "}
            <span className="italic text-[#7c6af0]">build?</span>
          </h1>
          <p
            className="text-[14px] text-[#6b5f8a]"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            Describe your idea and watch it become a real, deployable app in minutes.
          </p>
        </div>

        {/* Input Card */}
        <div
          className="rounded-[20px] overflow-hidden mb-8"
          style={{
            background: "rgba(255, 255, 255, 0.88)",
            border: "1px solid rgba(255, 255, 255, 0.9)",
            boxShadow: "0 4px 24px rgba(124, 106, 240, 0.1)",
          }}
        >
          <textarea
            ref={textareaRef}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="A simple app that..."
            disabled={isLoading}
            rows={5}
            className="w-full px-5 py-4 text-[16px] leading-relaxed bg-transparent border-0 focus:outline-none resize-none text-[#18151f] placeholder:text-[#6b5f8a]/50 disabled:opacity-50"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          />

          {/* Footer bar */}
          <div
            className="flex items-center justify-between px-5 py-3"
            style={{ borderTop: "1px solid rgba(124, 106, 240, 0.1)" }}
          >
            {/* Mode toggle */}
            <div
              className="flex items-center rounded-full p-1"
              style={{ background: "rgba(124, 106, 240, 0.08)" }}
            >
              <button
                onClick={() => setMode("build")}
                className="px-4 py-1.5 text-[13px] rounded-full transition-all"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  background: mode === "build" ? "#18151f" : "transparent",
                  color: mode === "build" ? "white" : "#6b5f8a",
                }}
              >
                Build
              </button>
              <button
                onClick={() => setMode("chat")}
                className="px-4 py-1.5 text-[13px] rounded-full transition-all"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  background: mode === "chat" ? "#18151f" : "transparent",
                  color: mode === "chat" ? "white" : "#6b5f8a",
                }}
              >
                Chat
              </button>
            </div>

            <button
              onClick={handleSubmit}
              disabled={!prompt.trim() || isLoading}
              className="flex items-center gap-2 px-6 py-2.5 text-[14px] font-medium text-white rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: "#18151f",
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  Start Building
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>

        {/* Recent Apps */}
        {recentProjects.length > 0 && (
          <div className="mb-10">
            <p
              className="text-[11px] uppercase tracking-wider text-[#6b5f8a] mb-4 text-center"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              Your recent apps
            </p>
            <div className="grid grid-cols-3 gap-3">
              {recentProjects.map((project) => (
                <button
                  key={project.id}
                  onClick={() => navigate(`/code-workspace?projectId=${project.id}`)}
                  className="text-left rounded-[16px] overflow-hidden transition-all hover:scale-[1.02]"
                  style={{
                    background: "rgba(255, 255, 255, 0.72)",
                    backdropFilter: "blur(12px)",
                    border: "1px solid rgba(255, 255, 255, 0.85)",
                  }}
                >
                  {/* Gradient Thumbnail */}
                  <div
                    className="h-16 w-full flex items-center justify-center"
                    style={{
                      background: `linear-gradient(135deg, hsl(${getHueFromString(project.name)}, 70%, 75%) 0%, hsl(${getHueFromString(project.name) + 30}, 60%, 85%) 100%)`,
                    }}
                  >
                    <span className="text-5xl font-light text-white/60" style={{ fontFamily: "'Instrument Serif', serif" }}>
                      {(project.name || "U").charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <h4
                        className="text-[14px] font-semibold text-[#18151f] truncate"
                        style={{ fontFamily: "'Instrument Serif', serif" }}
                      >
                        {project.name || "Untitled"}
                      </h4>
                      {project.isLive ? (
                        <span className="flex items-center gap-1 flex-shrink-0">
                          <span className="w-2 h-2 bg-[#059669] rounded-full animate-pulse" />
                          <span className="text-[10px] text-[#059669]">Live</span>
                        </span>
                      ) : (
                        <span className="text-[10px] text-[#6b5f8a] flex-shrink-0">Draft</span>
                      )}
                    </div>
                    <p className="text-[11px] text-[#6b5f8a] line-clamp-1 mb-1">
                      {project.description || "No description"}
                    </p>
                    <p className="text-[10px] text-[#6b5f8a]/70">
                      Edited {getRelativeTime(project.updated_at)}
                    </p>
                  </div>
                </button>
              ))}
            </div>
            <div className="text-center mt-4">
              <Link
                to="/my-apps"
                className="text-[13px] text-[#7c6af0] hover:underline"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                View all your apps →
              </Link>
            </div>
          </div>
        )}

        {/* Templates */}
        <div className="mb-10">
          <p
            className="text-[11px] uppercase tracking-wider text-[#6b5f8a] mb-4 text-center"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            Start with a template
          </p>
          <div className="grid grid-cols-2 gap-4">
            {STARTER_TEMPLATES.map((t) => (
              <button
                key={t.id}
                onClick={() => {
                  if (requireAuth()) return;
                  setPrompt(t.prompt);
                  textareaRef.current?.focus();
                }}
                disabled={isLoading}
                className="text-left rounded-[16px] overflow-hidden transition-all hover:scale-[1.02] disabled:opacity-50"
                style={{
                  background: "rgba(255, 255, 255, 0.72)",
                  backdropFilter: "blur(12px)",
                  border: "1px solid rgba(255, 255, 255, 0.85)",
                }}
              >
                {/* Full-bleed gradient preview */}
                <div
                  className="h-28 w-full flex items-center justify-center"
                  style={{
                    background: `linear-gradient(135deg, hsl(${getHueFromString(t.category)}, 65%, 72%) 0%, hsl(${getHueFromString(t.category) + 40}, 55%, 82%) 100%)`,
                  }}
                >
                  <div
                    className="w-12 h-12 text-white/90"
                    dangerouslySetInnerHTML={{ __html: t.iconSvg.replace('class="w-5 h-5"', 'class="w-12 h-12"') }}
                  />
                </div>
                <div className="p-4">
                  <p
                    className="text-[15px] font-semibold text-[#18151f] mb-1"
                    style={{ fontFamily: "'Instrument Serif', serif" }}
                  >
                    {t.name}
                  </p>
                  <p className="text-[12px] text-[#6b5f8a] line-clamp-1">
                    {t.description}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Inspiration */}
        {inspirationIdeas.length > 0 && (
          <div className="text-center">
            <p
              className="text-[11px] uppercase tracking-wider text-[#6b5f8a] mb-4"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              Need inspiration?
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {inspirationIdeas.map((idea) => (
                <Link
                  key={idea.id}
                  to="/startups"
                  className="px-3 py-1.5 text-[12px] rounded-full transition-colors hover:bg-white/80"
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    background: "rgba(255, 255, 255, 0.6)",
                    border: "1px solid rgba(255, 255, 255, 0.85)",
                    color: "#6b5f8a",
                  }}
                >
                  {idea.title} →
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
